import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, MapPin, Clock, Car, Copy, Check, RefreshCw, Navigation, Radio, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  confirmed: 'bg-blue-100 text-blue-800',
  driver_assigned: 'bg-indigo-100 text-indigo-800',
  en_route: 'bg-yellow-100 text-yellow-800',
  arriving: 'bg-orange-100 text-orange-800',
  in_service: 'bg-green-100 text-green-800',
  complete: 'bg-gray-100 text-gray-800',
};

const AdminTracking = () => {
  const navigate = useNavigate();
  const [activeTrackings, setActiveTrackings] = useState([]);
  const [recentCompleted, setRecentCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activatingId, setActivatingId] = useState(null);
  const [activateForm, setActivateForm] = useState({ driver_name: '', driver_phone: '', eta_minutes: '' });
  const [untracked, setUntracked] = useState([]);
  const [copiedToken, setCopiedToken] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const loadData = async () => {
    try {
      const [trackRes, bookingsRes] = await Promise.all([
        fetch(`${API}/api/tracking/admin/active`),
        fetch(`${API}/api/bookings`)
      ]);
      const trackData = await trackRes.json();
      const bookingsData = await bookingsRes.json();
      
      setActiveTrackings(trackData.active || []);
      setRecentCompleted(trackData.recent_completed || []);
      
      const trackedIds = new Set([...(trackData.active || []), ...(trackData.recent_completed || [])].map(t => t.booking_id));
      const untrackedBookings = (bookingsData.bookings || []).filter(b => !trackedIds.has(b.bookingId) && b.status !== 'cancelled');
      setUntracked(untrackedBookings);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleActivate = async (bookingId) => {
    try {
      const body = {};
      if (activateForm.driver_name) body.driver_name = activateForm.driver_name;
      if (activateForm.driver_phone) body.driver_phone = activateForm.driver_phone;
      if (activateForm.eta_minutes) body.eta_minutes = parseInt(activateForm.eta_minutes);
      
      const res = await fetch(`${API}/api/tracking/admin/activate/${bookingId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const data = await res.json();
      toast({ title: 'Tracking Activated', description: `Driver link: /driver/${data.driver_token}` });
      setActivatingId(null);
      setActivateForm({ driver_name: '', driver_phone: '', eta_minutes: '' });
      loadData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleStatusUpdate = async (bookingId, status, eta) => {
    try {
      const body = { status };
      if (eta) body.eta_minutes = parseInt(eta);
      await fetch(`${API}/api/tracking/admin/${bookingId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      toast({ title: 'Updated', description: `Status: ${status.replace('_', ' ')}` });
      loadData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const copyDriverLink = (token) => {
    const url = `${window.location.origin}/driver/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
    toast({ title: 'Copied!', description: 'Driver link copied to clipboard' });
  };

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-tracking">
      <header className="bg-black text-white py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Back</span>
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Live Tracking</h1>
              <p className="text-xs text-gray-400 hidden sm:block">Monitor & manage active rides</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-green-600/20 px-3 py-1 rounded-full">
              <Radio className="h-3 w-3 text-green-400 animate-pulse" />
              <span className="text-xs text-green-400 font-medium">{activeTrackings.length} Active</span>
            </div>
            <Button variant="outline" size="sm" onClick={loadData} className="border-gray-700 text-gray-300 hover:text-white">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Active Trackings */}
        {activeTrackings.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-black mb-4">Active Rides</h2>
            <div className="grid gap-4">
              {activeTrackings.map(t => (
                <Card key={t.booking_id} className="p-4 sm:p-5 border-gray-200" data-testid={`active-tracking-${t.booking_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[t.status] || 'bg-gray-100'}`}>
                        {t.status_label || t.status}
                      </span>
                      <span className="text-sm font-mono text-gray-500">{t.booking_id?.slice(0, 16)}...</span>
                      {t.eta_minutes && <span className="text-xs text-gray-500 flex items-center gap-1"><Clock className="h-3 w-3" />ETA: {t.eta_minutes}m</span>}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => copyDriverLink(t.driver_token)} className="text-xs" data-testid={`copy-driver-link-${t.booking_id}`}>
                      {copiedToken === t.driver_token ? <Check className="h-3 w-3 mr-1 text-green-600" /> : <Copy className="h-3 w-3 mr-1" />}
                      Driver Link
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-3">
                    <div><span className="text-gray-500 text-xs">Client:</span> <span className="font-medium">{t.client_name || 'N/A'}</span></div>
                    <div><span className="text-gray-500 text-xs">Vehicle:</span> <span className="font-medium">{t.vehicle_name || 'N/A'}</span></div>
                    <div><span className="text-gray-500 text-xs">Driver:</span> <span className="font-medium">{t.driver_name || 'Unassigned'}</span></div>
                    <div><span className="text-gray-500 text-xs">Pickup:</span> <span className="font-medium truncate">{t.pickup_location || 'N/A'}</span></div>
                  </div>
                  {/* Quick Status Buttons */}
                  <div className="flex flex-wrap gap-2">
                    {['en_route', 'arriving', 'in_service', 'complete'].map(s => (
                      <Button key={s} size="sm" variant={t.status === s ? 'default' : 'outline'}
                        className={`text-xs ${t.status === s ? 'bg-red-600' : 'border-gray-300'}`}
                        onClick={() => handleStatusUpdate(t.booking_id, s)}
                        disabled={t.status === s}
                        data-testid={`status-btn-${s}-${t.booking_id}`}>
                        {s.replace('_', ' ')}
                      </Button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Untracked Bookings */}
        {untracked.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-black mb-4">Untracked Bookings</h2>
            <div className="grid gap-3">
              {untracked.map(b => (
                <Card key={b.bookingId} className="p-4 border-gray-200" data-testid={`untracked-${b.bookingId}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <span className="text-sm font-mono text-gray-500">{b.bookingId?.slice(0, 16)}...</span>
                      <div className="flex flex-wrap gap-3 text-sm mt-1">
                        <span className="flex items-center gap-1 text-gray-600"><Car className="h-3 w-3" />{b.serviceType || 'N/A'}</span>
                        <span className="flex items-center gap-1 text-gray-600"><MapPin className="h-3 w-3" />{b.pickupLocation || 'N/A'}</span>
                      </div>
                    </div>
                    {activatingId === b.bookingId ? (
                      <div className="flex flex-col sm:flex-row gap-2 items-end">
                        <Input placeholder="Driver name" value={activateForm.driver_name} onChange={e => setActivateForm({...activateForm, driver_name: e.target.value})} className="h-8 text-sm w-36" />
                        <Input placeholder="Driver phone" value={activateForm.driver_phone} onChange={e => setActivateForm({...activateForm, driver_phone: e.target.value})} className="h-8 text-sm w-36" />
                        <Input placeholder="ETA (min)" type="number" value={activateForm.eta_minutes} onChange={e => setActivateForm({...activateForm, eta_minutes: e.target.value})} className="h-8 text-sm w-24" />
                        <Button size="sm" onClick={() => handleActivate(b.bookingId)} className="bg-red-600 hover:bg-red-700 text-xs" data-testid={`activate-btn-${b.bookingId}`}>
                          Activate
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setActivatingId(null)} className="text-xs">Cancel</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setActivatingId(b.bookingId)} className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white text-xs" data-testid={`start-tracking-${b.bookingId}`}>
                        <Navigation className="h-3 w-3 mr-1" /> Start Tracking
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Recent Completed */}
        {recentCompleted.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-black mb-4">Recently Completed</h2>
            <div className="grid gap-2">
              {recentCompleted.map(t => (
                <Card key={t.booking_id} className="p-3 border-gray-200 bg-gray-50" data-testid={`completed-${t.booking_id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="font-mono text-gray-500">{t.booking_id?.slice(0, 16)}...</span>
                      <span className="text-gray-600">{t.client_name}</span>
                    </div>
                    <span className="text-xs text-gray-400">{t.vehicle_name}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeTrackings.length === 0 && untracked.length === 0 && (
          <Card className="p-12 border-gray-200 text-center">
            <Navigation className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No active bookings to track. Bookings will appear here when clients book.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminTracking;
